/**
 * The context `Combobox` and `Autocomplete` each provide to the parts they
 * share (see `listboxParts.tsx`), plus the item helpers that go with it. A
 * component-free module, because React Fast Refresh only hot-reloads a file
 * whose exports are all components.
 */
import * as React from "react";

/** Control density of the root's input. The popup's density doesn't vary. */
export type ListboxSize = "sm" | "md" | "lg";

export interface ListboxContextValue {
  /** Id of the `<input>`, so the root's `<label>` can name it. */
  inputId: string;
  /**
   * The query the rows mark as matched: blank whenever the list on screen
   * isn't narrowed by it, so a mark never claims filtering that didn't happen.
   * Each root explains how it derives this.
   */
  highlightQuery: string;
  /** Rows in the whole, unfiltered list; absent when the root doesn't count. */
  itemCount?: number;
  size: ListboxSize;
  /**
   * The root's item-to-text function, erased to `unknown`: the context is
   * shared by every instantiation of the generic roots, so it can't be generic.
   */
  toString: (item: unknown) => string;
}

export const ListboxContext = React.createContext<ListboxContextValue | null>(
  null,
);

/** The root's context, for a part that is meaningless outside one. */
export function useListboxContext(part: string): ListboxContextValue {
  const context = React.useContext(ListboxContext);
  if (context === null) {
    throw new Error(
      `<${part}> must be rendered inside a <Combobox> or an <Autocomplete>. The parts read the input id, query, and size from it.`,
    );
  }
  return context;
}

function isPrimitive(value: unknown): value is string | number | boolean {
  return (
    typeof value === "string" ||
    typeof value === "number" ||
    typeof value === "boolean"
  );
}

/**
 * The text that stands for an item in a row: what a bare `Item` renders and
 * what the marks are checked against.
 *
 * Resolved in the order Base UI names an item in the input and in its default
 * filter: the caller's `itemToString`, then a `label` field, then a `value`
 * field, then a serialization. That keeps a row's text in step with the input
 * above it. The one departure: a `label` or `value` that isn't a string,
 * number, or Boolean is skipped, where Base UI would print it with `String`.
 */
export function resolveItemLabel<T>(
  item: T,
  itemToString?: (item: T) => string,
): string {
  if (item === null || item === undefined) {
    return "";
  }

  if (itemToString) {
    return itemToString(item);
  }

  if (typeof item === "object") {
    // Only a primitive field can become input text. A `label` holding a
    // ReactNode is exactly the case `String` would turn into "[object Object]".
    if ("label" in item && isPrimitive(item.label)) {
      return String(item.label);
    }
    if ("value" in item && isPrimitive(item.value)) {
      return String(item.value);
    }
    // Nothing conventional to read. Serialize rather than render the
    // "[object Object]" that `String` would give.
    try {
      return JSON.stringify(item);
    } catch {
      return String(item);
    }
  }

  return String(item);
}

/**
 * One labeled run of items. Base UI's own shape: only `items` is read, and any
 * other field (a label, an id) rides along to the `List` render function.
 */
export interface ListboxItemGroup<T> {
  items: readonly T[];
}

/**
 * Mirrors Base UI's internal test for grouped `items` (the first entry has an
 * `items` field), so a count reads the array the way Base UI does.
 */
function isGrouped<T>(
  items: readonly T[] | readonly ListboxItemGroup<T>[],
): items is readonly ListboxItemGroup<T>[] {
  const first: unknown = items[0];
  return typeof first === "object" && first !== null && "items" in first;
}

/** Selectable rows in `items`; when grouped, the items across every group. */
export function countRows<T>(
  items: readonly T[] | readonly ListboxItemGroup<T>[],
): number {
  return isGrouped(items)
    ? items.reduce((total, group) => total + group.items.length, 0)
    : items.length;
}
